import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState, useRef, useEffect, useMemo } from "react";
import { FormattedMessage as FM } from "react-intl";

import { CalendarIcon } from "components/Icons";
import FormField from "./FormField";
import Popover from "componentsv2/widgets/Popover";
import { cn } from "tools/ui";
import { normalizeDate, parseManualDate } from "tools/helpers";

const FORMAT = "d 'de' MMMM 'de' yyyy";

const MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const DAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// Get current local date in YYYY-MM-DD format
function getTodayLocal() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Convert YYYY-MM-DD string to local Date object
function toLocalDate(dateStr) {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;

  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

// Format for display ("26 de mayo de 2026")
function formatDisplay(dateStr) {
  if (!dateStr) return null;
  const date = toLocalDate(dateStr);
  if (isNaN(date.getTime())) return null;
  return format(date, FORMAT, { locale: es });
}

// Modern calendar component
function ModernCalendar({ selected, onSelect, onClose, minDate, maxDate }) {
  const todayStr = getTodayLocal();
  const [currentDate, setCurrentDate] = useState(() => {
    if (selected) return toLocalDate(selected);
    return toLocalDate(todayStr);
  });
  const [viewMode, setViewMode] = useState("days");
  const [yearPage, setYearPage] = useState(0); // Control year page navigation

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Generate years based on current page
  const startYear = currentYear - 6 + yearPage * 12;
  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  const goPrev = () => {
    if (viewMode === "days") {
      setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
    } else if (viewMode === "months") {
      setCurrentDate(new Date(currentYear - 1, currentMonth, 1));
    } else if (viewMode === "years") {
      // Navigate to previous year page
      setYearPage((prev) => prev - 1);
    }
  };

  const goNext = () => {
    if (viewMode === "days") {
      setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
    } else if (viewMode === "months") {
      setCurrentDate(new Date(currentYear + 1, currentMonth, 1));
    } else if (viewMode === "years") {
      // Navigate to next year page
      setYearPage((prev) => prev + 1);
    }
  };

  // Calculate days of the month
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const firstDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  const days = [];

  for (let i = 0; i < firstDayIndex; i++) {
    const day = prevMonthDays - firstDayIndex + i + 1;
    const date = new Date(currentYear, currentMonth - 1, day);
    days.push({ day, date, isCurrentMonth: false });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(currentYear, currentMonth, d);
    days.push({ day: d, date, isCurrentMonth: true });
  }

  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(currentYear, currentMonth + 1, i);
    days.push({ day: i, date, isCurrentMonth: false });
  }

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const isSelected = (date) => {
    if (!selected) return false;
    return formatDateKey(date) === selected;
  };

  const isToday = (date) => {
    return formatDateKey(date) === todayStr;
  };

  const isDisabled = (date) => {
    const dateKey = formatDateKey(date);
    if (minDate && dateKey < minDate) return true;
    if (maxDate && dateKey > maxDate) return true;
    return false;
  };

  const handleDateSelect = (date) => {
    const dateKey = formatDateKey(date);
    if (isDisabled(date)) return;
    onSelect(dateKey);
    onClose();
  };

  const handleToday = () => {
    onSelect(todayStr);
    onClose();
  };

  const handleClear = () => {
    onSelect(null);
    onClose();
  };

  // Switch to years view and reset page
  const handleYearsView = () => {
    setViewMode("years");
    setYearPage(0);
  };

  return (
    <div className="absolute top-full left-0 mt-2 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={goPrev}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div className="flex gap-2">
            {viewMode !== "years" && (
              <button
                onClick={() =>
                  setViewMode(viewMode === "months" ? "days" : "months")
                }
                className="text-base font-semibold text-gray-800 hover:text-gray-600 transition-colors"
              >
                {MONTHS_ES[currentMonth]}
              </button>
            )}
            <button
              onClick={handleYearsView}
              className="text-base font-semibold text-gray-800 hover:text-gray-600 transition-colors"
            >
              {viewMode === "years"
                ? `${years[0]} - ${years[years.length - 1]}`
                : currentYear}
            </button>
          </div>

          <button
            onClick={goNext}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Days View */}
        {viewMode === "days" && (
          <>
            <div className="grid grid-cols-7 gap-1 mb-3">
              {DAYS_ES.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days.map(({ day, date, isCurrentMonth }, idx) => {
                const selected_ = isSelected(date);
                const today_ = isToday(date);
                const disabled_ = isDisabled(date);

                return (
                  <button
                    type="button"
                    key={idx}
                    onClick={() => handleDateSelect(date)}
                    disabled={disabled_}
                    className={cn(
                      "relative h-10 w-10 rounded-xl text-sm font-medium transition-all duration-200 mx-auto",
                      "hover:scale-105 hover:shadow-md",
                      selected_ &&
                        "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200",
                      !selected_ &&
                        !disabled_ &&
                        today_ &&
                        "bg-blue-50 text-blue-600 font-bold border-2 border-blue-200",
                      !selected_ &&
                        !disabled_ &&
                        !today_ &&
                        isCurrentMonth &&
                        "text-gray-700 hover:bg-gray-100",
                      !selected_ &&
                        !disabled_ &&
                        !today_ &&
                        !isCurrentMonth &&
                        "text-gray-300 hover:bg-gray-50",
                      disabled_ &&
                        "opacity-40 cursor-not-allowed hover:scale-100",
                    )}
                  >
                    {day}
                    {today_ && !selected_ && (
                      <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Months View */}
        {viewMode === "months" && (
          <div className="grid grid-cols-3 gap-3">
            {MONTHS_ES.map((month, idx) => (
              <button
                type="button"
                key={month}
                onClick={() => {
                  setCurrentDate(new Date(currentYear, idx, 1));
                  setViewMode("days");
                }}
                className={cn(
                  "py-3 rounded-xl text-sm font-medium transition-all duration-200",
                  idx === currentMonth
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100",
                )}
              >
                {month.slice(0, 3)}
              </button>
            ))}
          </div>
        )}

        {/* Years View */}
        {viewMode === "years" && (
          <div className="grid grid-cols-3 gap-3">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => {
                  setCurrentDate(new Date(year, currentMonth, 1));
                  setViewMode("days");
                  setYearPage(0);
                }}
                className={cn(
                  "py-3 rounded-xl text-sm font-medium transition-all duration-200",
                  year === currentYear
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100",
                )}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer with actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-between">
        <button
          onClick={handleToday}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          Hoy
        </button>
        <button
          onClick={handleClear}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          Limpiar
        </button>
        <button
          onClick={onClose}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

// Main component
function DateCalendar({
  name,
  label,
  onChange,
  validate,
  field,
  data,
  readOnly,
  placeholder = "dd/mm/aa",
  minDate,
  maxDate,
  colSpan,
  model,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const popoverShow = useMemo(() => ({ value: !!inputError }), [!!inputError]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Sync input with current value
  useEffect(() => {
    const raw = typeof data === "object" && "value" in data ? data.value : data;
    if (raw === "Formato de Fecha inválido") return;
    const safeValue = normalizeDate(raw);
    if (safeValue) {
      const date = toLocalDate(safeValue);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2); // Last 2 digits only
      setInputValue(`${day}/${month}/${year}`);
    } else {
      setInputValue("");
    }
    setInputError("");
  }, [data]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getCurrentValue = () => {
    if (!data) return null;
    if (typeof data === "object" && "value" in data) {
      if (data.value === "Formato de Fecha inválido") return null;
      return data.value;
    }
    return data;
  };

  const setCurrentValue = (newValue) => {
    if (!data) return;
    if (typeof data === "object" && "value" in data) {
      data.value = newValue;
    }
    if (onChange) {
      onChange(name, newValue);
    }
  };

  const safeValue = normalizeDate(getCurrentValue());
  const displayValue = formatDisplay(safeValue);

  const handleOpen = () => {
    if (readOnly) return;
    setIsOpen(true);
  };

  const handleSelect = (value) => {
    setCurrentValue(value);
    setIsOpen(false);
    setInputError("");
  };

  // Handle manual input change
  const handleInputChange = (e) => {
    setInputError("");
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 4) formatted += "/";
      formatted += digits[i];
    }
    setInputValue(formatted);
  };

  // Handle blur or Enter on input
  const handleInputSubmit = () => {
    if (!inputValue.trim()) {
      setCurrentValue(null);
      setInputError("");
      return;
    }

    const parsedDate = parseManualDate(inputValue);
    if (!parsedDate) {
      setInputError("party.party.validation.invalid_date");
      setCurrentValue("Formato de Fecha inválido");
      return;
    }

    if (validate) {
      const result = validate({ value: parsedDate });
      if (typeof result === "string") {
        setInputError(result);
        setCurrentValue("Formato de Fecha inválido");
        return;
      }
    }

    // Check date restrictions
    if (minDate && parsedDate < minDate) {
      setInputError(`La fecha debe ser posterior a ${formatDisplay(minDate)}`);
      return;
    }
    if (maxDate && parsedDate > maxDate) {
      setInputError(`La fecha debe ser anterior a ${formatDisplay(maxDate)}`);
      return;
    }

    setCurrentValue(parsedDate);
    setInputError("");
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  const handleInputBlur = () => {
    handleInputSubmit();
  };

  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <div ref={containerRef} className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            disabled={readOnly}
            placeholder={placeholder}
            className={cn(
              "flex items-center w-full px-4 py-2.5 text-sm rounded-xl transition-all duration-200",
              "border border-gray-200",
              "focus:outline-none focus:ring-2 focus:border-transparent",
              readOnly
                ? "bg-gray-50 cursor-not-allowed"
                : "bg-white hover:border-gray-300",
              isOpen && "ring-1 border-transparent",
              inputError && "border-red-300 focus:ring-red-200",
            )}
          />
          <button
            type="button"
            onClick={handleOpen}
            disabled={readOnly}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <CalendarIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {inputError && (
          <Popover
            show={popoverShow}
            className="bg-gray-50/90! z-50 min-w-64 mt-1 rounded-sm! shadow-xl border border-neutral-300"
          >
            <p className="text-red-600 text-xs">
              {inputError.startsWith("party.") ? (
                <FM id={inputError} />
              ) : (
                inputError
              )}
            </p>
          </Popover>
        )}

        {isOpen && (
          <ModernCalendar
            selected={safeValue}
            onSelect={handleSelect}
            onClose={() => setIsOpen(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </div>
    </FormField>
  );
}

export { ModernCalendar };
export default DateCalendar;
